import React from 'react';
import { Box } from '@indeed/ifl-components';

import { paneSizes } from './commonStyles';

// SX object for InboxPageWrapper
const inboxPageWrapperSx = {
    fontFamily: 'default',
    backgroundColor: 'neutral.200',
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
    position: 'relative' as const,
    '@media (min-width: 480px)': {
        padding: 4,
    },
    '@media (min-width: 1280px)': {
        padding: '16px 64px 0 64px',
    },
    'h1, h2, h3, h4, ul, li': {
        fontFamily: 'default',
    },
} as const;

const InboxPageWrapper: React.FC<React.PropsWithChildren<any>> = ({ children, ...props }) => (
    <Box sx={inboxPageWrapperSx} {...props}>
        {children}
    </Box>
);

const mainPanesBorderRadius = '.75rem .75rem 0 0';

// SX object for DetailPaneWrapper
const detailPaneWrapperSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: '1 1 auto',
    backgroundColor: 'neutral.0',
    border: '1px solid',
    borderColor: 'neutral.300',
    borderRadius: mainPanesBorderRadius,
    '@media (min-height: 480px)': {
        '@media (min-width: 768px)': {
            flexBasis: paneSizes.sidePaneWidth,
            maxInlineSize: paneSizes.sidePaneWidth,
        },
    },
} as const;

const DetailPaneWrapper: React.FC<React.PropsWithChildren<any>> = ({ children, ...props }) => (
    <Box sx={detailPaneWrapperSx} {...props}>
        {children}
    </Box>
);

// SX object for ConversationListPaneWrapper
const conversationListPaneWrapperSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: 'neutral.0',
    inlineSize: '100%',
    borderRadius: mainPanesBorderRadius,
    '@media (min-height: 480px)': {
        '@media (min-width: 768px)': {
            border: '1px solid',
            borderColor: 'neutral.300',
            marginInlineEnd: 4,
            flexBasis: paneSizes.sidePaneWidth,
            maxInlineSize: paneSizes.sidePaneWidth,
        },
    },
} as const;

const ConversationListPaneWrapper: React.FC<React.PropsWithChildren<any>> = ({
    children,
    ...props
}) => (
    <Box sx={conversationListPaneWrapperSx} {...props}>
        {children}
    </Box>
);

// SX function for ConversationPaneWrapper (handles dynamic isExpanded prop)
const getConversationPaneWrapperSx = (isExpanded = false) => ({
    backgroundColor: 'neutral.0',
    insetBlockEnd: 0,
    insetInlineStart: 0,
    position: 'absolute' as const,
    insetInlineEnd: 0,
    insetBlockStart: 0,
    overflowY: 'auto' as const,
    inlineSize: '100vw',
    borderRadius: mainPanesBorderRadius,
    '@media (min-height: 480px)': {
        '@media (min-width: 768px)': {
            position: 'relative' as const,
            inlineSize: 'auto',
            border: '1px solid',
            borderColor: 'neutral.300',
            marginInlineEnd: isExpanded ? 0 : 4,
            flexBasis: isExpanded ? paneSizes.centralPaneExpandedWidth : paneSizes.centralPaneWidth,
            maxInlineSize: isExpanded
                ? paneSizes.centralPaneExpandedWidth
                : paneSizes.centralPaneWidth,
        },
        '&.is-splash': {
            display: 'flex',
        },
    },
});

interface ConversationPaneWrapperProps extends React.PropsWithChildren<any> {
    isExpanded?: boolean;
}

const ConversationPaneWrapper: React.FC<ConversationPaneWrapperProps> = ({
    children,
    isExpanded = false,
    ...props
}) => (
    <Box sx={getConversationPaneWrapperSx(isExpanded)} {...props}>
        {children}
    </Box>
);

// SX object for NoConversationSelectedWrapper
const noConversationSelectedWrapperSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: mainPanesBorderRadius,
    '@media (min-height: 480px)': {
        '@media (min-width: 768px)': {
            position: 'relative' as const,
            inlineSize: 'auto',
            border: '1px solid',
            borderColor: 'neutral.300',
            marginInlineEnd: 0,
            flexBasis: paneSizes.centralPaneExpandedWidth,
            maxInlineSize: paneSizes.centralPaneExpandedWidth,
            backgroundColor: 'neutral.0',
        },
    },
} as const;

const NoConversationSelectedWrapper: React.FC<React.PropsWithChildren<any>> = ({
    children,
    ...props
}) => (
    <Box sx={noConversationSelectedWrapperSx} {...props}>
        {children}
    </Box>
);

export {
    InboxPageWrapper,
    DetailPaneWrapper,
    ConversationListPaneWrapper,
    ConversationPaneWrapper,
    NoConversationSelectedWrapper,
};
